/*
  Blog article reading aids:
  - builds the "In this article" contents (side rail on wide screens, collapsible list on small ones)
  - highlights the section being read and counts down the minutes left
  - drives the thin progress bar at the top of the page
  - places the product card between two sections near the middle
  - wraps tables so they scroll inside the reading column
*/
(() => {
  const body = document.querySelector('[data-article-body]');
  const content = body && body.querySelector('[data-article-content]');
  if (!content) return;

  const WORDS_PER_MINUTE = 200;
  const totalWords = Number(body.dataset.words) || 0;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  // Ancestor of `el` that is a direct child of the content root (or null).
  const topLevel = (el) => {
    let node = el;
    while (node && node.parentElement !== content) node = node.parentElement;
    return node;
  };

  // Tables: scroll sideways inside the column instead of overflowing it.
  content.querySelectorAll('table').forEach((table) => {
    if (table.parentElement.classList.contains('article-table')) return;
    const wrap = document.createElement('div');
    wrap.className = 'article-table';
    wrap.tabIndex = 0;
    wrap.setAttribute('role', 'region');
    wrap.setAttribute('aria-label', 'Table');
    table.parentNode.insertBefore(wrap, table);
    wrap.appendChild(table);
  });

  // Answer-first callout: a paragraph that opens with "Quick answer".
  const firstStrong = content.querySelector('p > strong:first-child, p > b:first-child');
  if (firstStrong && /^quick answer/i.test(firstStrong.textContent.trim())) {
    firstStrong.parentElement.classList.add('article-answer');
  }

  // Sections come from the article's h2s.
  const headings = [...content.querySelectorAll('h2')].filter((h) => h.textContent.trim());
  const slug = (text) =>
    text
      .toLowerCase()
      .replace(/[’']/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 60);
  const used = new Set();
  headings.forEach((h) => {
    if (!h.id) {
      let id = slug(h.textContent) || 'section';
      while (used.has(id) || document.getElementById(id)) id += '-2';
      h.id = id;
    }
    used.add(h.id);
  });

  // Words before each heading, so "min left" tracks where the reader is.
  const wordsBefore = (() => {
    if (!headings.length || !totalWords) return [];
    const range = document.createRange();
    range.setStart(content, 0);
    return headings.map((h) => {
      range.setEndBefore(h);
      return range.toString().split(/\s+/).filter(Boolean).length;
    });
  })();

  // Product card: before the middle section, only when it sits cleanly between blocks.
  const nudgeTemplate = document.querySelector('template[data-article-nudge]');
  if (nudgeTemplate && headings.length >= 4) {
    const anchor = topLevel(headings[Math.floor(headings.length / 2)]);
    if (anchor && anchor !== content.firstElementChild) {
      content.insertBefore(nudgeTemplate.content.cloneNode(true), anchor);
    }
  }

  // Contents lists.
  const rail = body.querySelector('[data-article-toc]');
  const railList = body.querySelector('[data-article-toc-list]');
  const inline = body.querySelector('[data-article-toc-inline]');
  const inlineList = body.querySelector('[data-article-toc-inline-list]');
  const minutesLeft = body.querySelector('[data-article-left]');
  const railLinks = [];

  if (headings.length >= 3) {
    headings.forEach((h) => {
      const label = h.textContent.trim();
      [railList, inlineList].forEach((list) => {
        if (!list) return;
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.href = `#${h.id}`;
        a.textContent = label;
        li.appendChild(a);
        list.appendChild(li);
        if (list === railList) railLinks.push(a);
      });
    });

    const count = body.querySelector('[data-article-toc-count]');
    if (count) count.textContent = `${headings.length} sections`;

    // On small screens the contents sit just before the first section.
    const firstSection = topLevel(headings[0]);
    if (inline && firstSection && firstSection !== content.firstElementChild) {
      content.insertBefore(inline, firstSection);
    } else if (inline) {
      content.insertBefore(inline, content.firstChild);
    }
    rail.hidden = false;
    if (inline) inline.hidden = false;
    body.classList.add('article-body--has-toc');

    // Close the small-screen list after a jump so the reader lands on the text.
    inlineList.addEventListener('click', (e) => {
      if (e.target.closest('a')) inline.open = false;
    });
  }

  // Scroll tracking: progress bar, current section, minutes left.
  const bar = document.querySelector('[data-article-progress]');
  let current = -1;
  let ticking = false;

  const update = () => {
    ticking = false;
    const rect = content.getBoundingClientRect();
    const viewport = window.innerHeight;
    const travelled = viewport * 0.35 - rect.top;
    const progress = Math.min(1, Math.max(0, travelled / Math.max(1, rect.height - viewport * 0.35)));
    if (bar) bar.style.transform = `scaleX(${progress})`;

    if (!railLinks.length) return;

    let active = -1;
    for (let i = 0; i < headings.length; i++) {
      if (headings[i].getBoundingClientRect().top <= viewport * 0.35) active = i;
      else break;
    }
    if (active !== current) {
      current = active;
      railLinks.forEach((link, i) => {
        link.classList.toggle('is-read', i < active);
        if (i === active) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      });
    }

    if (minutesLeft && totalWords) {
      const read = Math.max(wordsBefore[active] || 0, Math.round(totalWords * progress));
      const left = Math.ceil((totalWords - read) / WORDS_PER_MINUTE);
      minutesLeft.textContent = progress >= 0.98 || left <= 0 ? 'Finished' : `${left} min left`;
    }
  };

  const onScroll = () => {
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(update);
    }
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  update();

  // Smooth jump for contents links unless the reader prefers reduced motion.
  body.addEventListener('click', (e) => {
    const link = e.target.closest('.article-toc a, .article-toc-inline a');
    if (!link) return;
    const target = document.getElementById(decodeURIComponent(link.hash.slice(1)));
    if (!target) return;
    e.preventDefault();
    target.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'start' });
    history.replaceState(null, '', link.hash);
    target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  });
})();
